import { useCallback, useEffect, useState } from "react";
import useGraphQLClient from "../hooks/useGraphQLClient";
import {
  READ_SOPS_AWAITING_ADMIN_APPROVAL,
  READ_MY_SOPS_TO_APPROVE,
} from "../graph/projects/queries";
import {
  ASSIGN_SOP_APPROVERS,
  APPROVE_SOP,
  REJECT_SOP,
} from "../graph/projects/mutations";

export function useSOPApprovals({ tenantId, userId } = {}) {
  const client = useGraphQLClient();

  const [adminApprovalSOPs, setAdminApprovalSOPs] = useState([]);
  const [myApprovalSOPs, setMyApprovalSOPs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAdminApprovalSOPs = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await client.request(READ_SOPS_AWAITING_ADMIN_APPROVAL, {
        filter: { tenantId },
      });

      setAdminApprovalSOPs(data.readSOPsAwaitingAdminApproval || []);
    } catch (err) {
      console.error("Failed to load admin SOP approvals", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [client, tenantId]);

  const loadMyApprovalSOPs = useCallback(async () => {
    if (!tenantId || !userId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await client.request(READ_MY_SOPS_TO_APPROVE, {
        tenantId,
        userId,
      });

      setMyApprovalSOPs(data.readMySOPsToApprove || []);
    } catch (err) {
      console.error("Failed to load my SOP approvals", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [client, tenantId, userId]);

  const assignSOPApprovers = async (projectId, approverUserIds) => {
    const data = await client.request(ASSIGN_SOP_APPROVERS, {
      input: {
        projectId,
        approverUserIds,
      },
    });

    await loadAdminApprovalSOPs();

    return data.assignSOPApprovers;
  };

  const approveSOP = async (projectId) => {
    if (!userId) {
      throw new Error("Cannot approve SOP without current user ID");
    }

    const data = await client.request(APPROVE_SOP, {
      input: {
        projectId,
        approverUserId: userId,
      },
    });

    await loadMyApprovalSOPs();

    return data.approveSOP;
  };

  const rejectSOP = async (projectId, rejectionReason) => {
  if (!userId) {
    throw new Error("Cannot reject SOP without current user ID");
  }

  const data = await client.request(REJECT_SOP, {
    input: {
      projectId,
      approverUserId: userId,
      rejectionReason,
    },
  });

  await loadMyApprovalSOPs();

  return data.rejectSOP;
};

  useEffect(() => {
    loadAdminApprovalSOPs();
  }, [loadAdminApprovalSOPs]);

  useEffect(() => {
    loadMyApprovalSOPs();
  }, [loadMyApprovalSOPs]);

  return {
    adminApprovalSOPs,
    myApprovalSOPs,
    loading,
    error,
    loadAdminApprovalSOPs,
    loadMyApprovalSOPs,
    assignSOPApprovers,
    approveSOP,
    rejectSOP,
  };
}